import type { Configuration } from "@azure/msal-browser";

/**
 * Where the browser signs in and what it asks for.
 *
 * The tenant is pinned in the authority: the app must not accept a sign-in from anywhere else.
 * That is not the only line of defence - Easy Auth on the three function apps validates the
 * issuer as tenant-specific as well - but it is the one that decides what the user is offered.
 *
 * Two options the configuration documentation still lists are absent on purpose: msal-browser v5
 * removed both from these types. The first became an argument of `handleRedirectPromise()`, where it
 * defaults to true - which is what `msalClient.ts` relies on by calling it bare, so the user still
 * lands back on the page that started the sign-in. The second was dropped outright as an IE-era
 * workaround; the temporary auth state now lives in sessionStorage whatever `cacheLocation` says.
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_SPA_CLIENT_ID ?? "",
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_ENTRA_TENANT_ID ?? ""}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    /*
     * sessionStorage, not localStorage: the token dies with the tab, which is the safer half of
     * the trade. The price is now visible, because the silent iframe that used to cover it is
     * gone (it cost ten seconds and delivered nothing - see AuthContext.tsx): a new tab starts
     * without an account and needs one click on "Login", which passes through Entra without a
     * prompt while a session there exists. Moving to localStorage would make a new tab - and
     * tomorrow morning - silent again, at the price of a refresh token that outlives the tab.
     */
    cacheLocation: "sessionStorage",
  },
};

/** The scope the three function apps accept as their audience. */
export const apiScopes: string[] = [import.meta.env.VITE_FUNCTIONS_API_SCOPE ?? ""];
